import type {
	AiContextAttachment,
	AiContextAttachmentType,
} from "@productlane/lib/src/ai"
import { AI_CONTEXT_ATTACHMENT_LIMIT } from "@productlane/lib/src/ai"

import type { JSONContent } from "@tiptap/core"

export interface ContextMentionItem {
	type: AiContextAttachmentType
	id: string
	label: string
	// Identifier, email, or status shown left of the label in the list.
	prefix?: string
	// Colour and Linear status the row's leading mark reads, when the entity
	// carries them.
	color?: string | null
	status?: string | null
	// Avatar or logo for people and companies; icon name for entities that
	// carry one of their own (docs articles, projects).
	imageUrl?: string | null
	icon?: string | null
	// A thread's channel, so the row shows the same mark as the inbox.
	origin?: string | null
}

export const CONTEXT_MENTION_GROUP_LABELS: Record<
	AiContextAttachmentType,
	string
> = {
	issue: "Issues",
	project: "Projects",
	thread: "Threads",
	contact: "Contacts",
	company: "Companies",
	article: "Docs",
	changelog: "Changelog",
	user: "People",
}

export const contextMentionKey = (item: {
	type: AiContextAttachmentType
	id: string
}) => `${item.type}:${item.id}`

// A mention node renders as a badge, so its text has to read as one token when
// the doc is flattened for the model and for the transcript bubble.
export const contextMentionText = (attrs: {
	label?: string | null
	prefix?: string | null
}) => `@${attrs.prefix ? `${attrs.prefix} ` : ""}${attrs.label ?? ""}`.trimEnd()

const nodeText = (node: JSONContent): string => {
	if (node.type === "contextMention") {
		return contextMentionText({
			label: node.attrs?.label as string | undefined,
			prefix: node.attrs?.prefix as string | undefined,
		})
	}
	if (node.type === "text") return node.text ?? ""
	if (node.type === "hardBreak") return "\n"
	return (node.content ?? []).map(nodeText).join("")
}

export const composerDocToText = (doc: JSONContent): string =>
	(doc.content ?? [])
		.map((block) => nodeText(block))
		.join("\n")
		.replace(/\n{3,}/g, "\n\n")
		.trim()

// Duplicates collapse to one attachment: pinning the same issue twice costs a
// second resolution on the server and tells the model nothing new.
export const composerDocToAttachments = (
	doc: JSONContent,
): AiContextAttachment[] => {
	const found: AiContextAttachment[] = []
	const seen = new Set<string>()

	const walk = (node: JSONContent) => {
		if (node.type === "contextMention") {
			const type = node.attrs?.type as AiContextAttachmentType | undefined
			const id = node.attrs?.id as string | undefined
			const label = node.attrs?.label as string | undefined
			const prefix = node.attrs?.prefix as string | undefined
			if (type && id) {
				const key = contextMentionKey({ type, id })
				if (!seen.has(key)) {
					seen.add(key)
					found.push({
						type,
						id,
						label: label?.slice(0, 200) || id,
						...(prefix ? { prefix } : {}),
					})
				}
			}
		}
		for (const child of node.content ?? []) walk(child)
	}

	walk(doc)
	return found.slice(0, AI_CONTEXT_ATTACHMENT_LIMIT)
}
